import React from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { X, CheckCircle } from "lucide-react";
import ModalOverlay from "../ModalOverlay/ModalOverlay.tsx";
import { closeInviteToRegisterModal } from "../../../store/actions/profileActions";
import { fireAnalytics } from "../../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../../utils/analyticsHelpers.ts";
import styles from "./InviteToRegisterModal.module.scss";

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { delay: 0.1, duration: 0.3, ease: "easeOut" },
  },
};

type InviteToRegisterContext = "after_identity_photos" | "after_work_bag_skip";

interface InviteToRegisterModalProps {
  inviteToRegisterModal: false | { open: true; context: InviteToRegisterContext };
  closeInviteToRegisterModal: () => void;
}

const InviteToRegisterModal: React.FC<InviteToRegisterModalProps> = ({
  inviteToRegisterModal,
  closeInviteToRegisterModal,
}) => {
  const navigate = useNavigate();
  const isOpen = Boolean(inviteToRegisterModal && inviteToRegisterModal.open);
  const context: InviteToRegisterContext =
    inviteToRegisterModal && inviteToRegisterModal.open
      ? inviteToRegisterModal.context
      : "after_identity_photos";

  const fireDismissEvent = (dismiss_action: string) => {
    fireAnalytics("invite_to_register_modal_dismiss", {
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      context,
      category: "onboarding",
      action: "modal_dismiss",
      dismiss_action,
    });
  };

  const handleRegister = () => {
    fireAnalytics("invite_to_register_modal_cta_click", {
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      context,
      category: "onboarding",
      action: "cta_click",
      cta_label: "crear_cuenta_edify",
    });
    closeInviteToRegisterModal();
    navigate("/register");
  };

  const handleNoThanks = () => {
    fireDismissEvent("no_gracias");
    closeInviteToRegisterModal();
    navigate("/bolsadetrabajo/registro-exitoso");
  };

  const handleVerOfertas = () => {
    fireDismissEvent("ver_ofertas");
    closeInviteToRegisterModal();
    navigate("/bolsadetrabajo");
  };

  const handleCloseButtonOrOverlay = () => {
    fireDismissEvent("close");
    closeInviteToRegisterModal();
    navigate("/bolsadetrabajo/registro-exitoso");
  };

  if (!isOpen) return null;

  const isAfterPhotos = context === "after_identity_photos";

  return (
    <ModalOverlay isOpen={true} onClose={handleCloseButtonOrOverlay}>
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.modalBody}>
          <button
            className={styles.closeButton}
            onClick={handleCloseButtonOrOverlay}
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>

          <div className={styles.content}>
            <div className={styles.iconContainer}>
              <CheckCircle size={48} className={styles.successIcon} />
            </div>

            {isAfterPhotos ? (
              <>
                <h3 className={styles.title}>¡Imágenes enviadas!</h3>
                <div className={styles.bodyText}>
                  El proceso de validación de tu identidad no es inmediato.
                  Recibirás una notificación en tu correo electrónico una vez
                  que se haya completado. Revisá también la carpeta de spam por
                  si no lo encontrás.
                </div>
                <div className={styles.ctaBlock}>
                  <p className={styles.ctaTitle}>Creá tu cuenta en Edify</p>
                  <p className={styles.ctaSubtitle}>
                    Gratis por 60 días, sin tarjeta de crédito. Accedé a todas
                    las funcionalidades de la plataforma.
                  </p>
                </div>
              </>
            ) : (
              <>
                <h3 className={styles.title}>Creá tu cuenta en Edify</h3>
                <div className={styles.bodyText}>
                  Sumá más: accedé a la plataforma completa gratis por 60 días,
                  sin tarjeta de crédito. Conectá con empresas, gestioná tus
                  postulaciones y aprovechá todas las funcionalidades de Edify.
                </div>
              </>
            )}
          </div>

          <div className={styles.options}>
            <button
              className={styles.primaryButton}
              onClick={handleRegister}
              type="button"
            >
              Crear cuenta en Edify (gratis 60 días)
            </button>
            <button
              className={styles.secondaryButton}
              onClick={handleNoThanks}
              type="button"
            >
              No, gracias
            </button>
            <button
              className={styles.linkCta}
              onClick={handleVerOfertas}
              type="button"
            >
              Ver ofertas de trabajo
            </button>
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

const mapStateToProps = (state: {
  general: {
    inviteToRegisterModal:
      | false
      | { open: true; context: InviteToRegisterContext };
  };
}) => ({
  inviteToRegisterModal: state.general.inviteToRegisterModal,
});

export default connect(mapStateToProps, { closeInviteToRegisterModal })(
  InviteToRegisterModal
);
